export type FactionId = 'celestial' | 'runic' | 'techno' | 'elemental' | 'dominion';
export type Subject = 'math' | 'chinese' | 'english' | 'nature' | 'social';
export type TextbookPublisher = '康軒' | '南一' | '翰林';
/**
 * 學年度小數標記:3.1 = 三上、3.2 = 三下、4.1 = 四上、4.2 = 四下。
 * 對齊課程 `KPEntry.grade`,驅動「出考卷中心」依當前學年過濾單元。
 * 單一真相在此(exam-center-route 與 grade-semester 皆 import 本型別)。
 */
export type GradeSemester = 3.1 | 3.2 | 4.1 | 4.2;

/** Five faction accent colours used for avatar identification. */
export const AVATAR_COLORS = ['#4FC3F7', '#CE93D8', '#80CBC4', '#FFE082', '#EF9A9A'] as const;
export type AvatarColor = typeof AVATAR_COLORS[number];

export interface ProfileMeta {
  /** 'default' for the first migrated profile, otherwise a uuid v4 string. */
  id: string;
  nickname: string;
  createdAt: number;  // Unix timestamp ms
  avatarColor?: AvatarColor;
}

export interface FamilyState {
  familyCode?: string;
  profiles: ProfileMeta[];
  activeProfileId: string;
}

export interface Faction {
  id: FactionId;
  displayName: string;       // 「星算門」
  emoji: string;             // 「⚡」
  colorPrimary: number;      // 0x4FC3F7
}

export interface MistakeEntry {
  questionId: string;               // question's hash / id
  kp: string;
  subject: Subject;
  prompt: string;                   // text snapshot
  correctAnswer: string | number;
  userAnswer: string | number | null;
  /**
   * 一句到數句的「為什麼錯 / 怎麼算」說明,讓錯題本能教而不只列題。來源:OCR
   * 自動批改 (ExtractedItem.explanation) 或數學產生器 (GeneratedQuestion.explanation)。
   * Optional — 舊存檔沒有此欄不可炸;錯題本詳情會 fallback 顯示。
   */
  explanation?: string;
  errorType?: 'careless' | 'concept' | 'method' | 'time';  // optional self-tag
  timestamp: number;
  retried: boolean;                 // user redid this? mark for removal next session
}

/**
 * 一題已歸檔的考卷題 — 拍照上傳 → 家長確認後的快照(discard 的不歸檔)。
 * boss 2026-06-10「考卷沒錯的地方就沒了」:答對的題也要永久保存,之後隨時
 * 可出變形練習卷,不再批改完就蒸發。
 */
export interface ArchivedExamItem {
  subject: Subject;
  kp: string;
  kind: 'mcq' | 'fill';
  prompt: string;
  options?: (string | number)[];
  correctAnswer: number | string;
  /** 小孩當時手寫的作答(OCR 讀到才有)。 */
  studentAnswer?: string;
  explanation?: string;
  /** 圖表結構化資料(learn 層 Figure 的原樣 JSON;render 前用 normalizeFigure 驗)。 */
  figure?: unknown;
  /** 家長確認結果。 */
  decision: 'correct' | 'wrong' | 'careless';
}

/** 一張已歸檔的考卷(拍照上傳批改完整卷保存,FIFO 上限見 exam-upload/archive.ts)。 */
export interface ArchivedExam {
  id: string;
  /** 批改確認時間 ISO(之後可供「考試規劃」按日期歸範圍)。 */
  dateISO: string;
  items: ArchivedExamItem[];
}

export interface PlayerProgress {
  version: number;
  studentName: string;
  /**
   * 主人翁 / 英雄身份 (玩家所選門派). Drives the in-battle active-skill 玩法 +
   * 戰車外觀 + 立繪 (see docs/spec/HERO_SYSTEM_V1.md §1.1). Distinct from the
   * stage faction (which building the kid is attacking, passed via the battle
   * scene's `hubFactionId`). 學習與遊戲分離鐵則: this is a pure-game identity —
   * 選英雄 NEVER pops a question. Optional + defaults to 'celestial' (the
   * default-unlocked starter) so legacy saves migrate cleanly. Added in v28.
   * Use `getSelectedHeroId` / `setSelectedHeroId` helpers in progress.ts.
   */
  selectedHeroId?: FactionId;
  totalXP: number;
  factionXP: Record<FactionId, number>;
  freeXP: number;            // unallocated battle rewards
  textbookPrefs: Partial<Record<Subject, TextbookPublisher>>;
  unlockedSkills: string[];  // skill ids from shared/skills.ts
  currentLoadout: string[];  // up to 4 skill ids; '' or missing slots ignored
  createdAt: string;         // ISO date
  lastSavedAt: string;       // ISO date
  /** Per-KP mastery tracking. Required from v3 onward. */
  studentKPs: StudentKP[];
  /** XP earned per calendar day. Key format: 'YYYY-MM-DD'. Added in v4. */
  dailyXP: Record<string, number>;
  /** Login streak tracking. Added in v4.
   *  C01 (v19) added `longest` — historical longest streak the kid has ever
   *  reached. Required from v19+. Updated by `tickStreakOnLoad` whenever the
   *  current run exceeds the previous record. Used by the streak banner +
   *  parent dashboard "最佳紀錄" line for loss-aversion framing. */
  streak: { days: number; lastLoginISO: string; longest: number };
  /** Audio volume settings. Added in v5. `masterVolume` added in G08 (optional, defaults to 1.0). */
  audio: { bgmVolume: number; sfxVolume: number; masterVolume?: number };
  /** Whether the player has completed the interactive tutorial. Added in v6. */
  tutorialDone: boolean;
  /** Skill level per skill id (1–5). 0 or missing = not unlocked. Added in v7. */
  skillLevels: Record<string, number>;
  /** Wrong-answer history for the mistake book. Max 100 entries FIFO. Added in v8. */
  mistakes: MistakeEntry[];
  /**
   * 拍照上傳批改完的整卷歸檔(答對的也存)。Optional → 舊存檔免遷移,
   * 讀寫端一律防禦性初始化。FIFO 新卷在前。Added 2026-06-10。
   */
  uploadedExams?: ArchivedExam[];
  /** Slay-Spire campaign progress. Added in v9. */
  campaignState: {
    chapter: number;           // 1-5
    currentNodeId: string | null;
    visitedNodes: string[];    // completed node ids
    graphSeed: number;         // RNG seed for current chapter
  };
  /** Cross-device sync family code (6 uppercase alphanumeric chars). Added in v10. */
  familyCode?: string;
  /** Unix timestamp (ms) of last successful sync to Worker. Added in v10. */
  lastSyncedAt?: number;
  /** Infinite Tower run history. Max 50 entries FIFO, newest first. Added in v11. */
  towerHistory: TowerRun[];
  /** Boss IDs the player has defeated at least once. Added in v12. */
  bossesDefeated: string[];
  /** Streak reward tier IDs already claimed (e.g. 'streak-1','streak-3','streak-7'...). Added in v12. */
  streakRewardClaimed: string[];
  /** Daily challenge state. Added in v12. */
  dailyChallenge: {
    /** ISO date string (YYYY-MM-DD) when the current task was assigned. */
    assignedDate: string;
    /** Task id (e.g. 'fast-kill'). */
    taskId: string;
    /** Whether the task was completed on assignedDate. */
    completed: boolean;
  } | null;
  /** The profile id this progress snapshot belongs to. Added in v13. Defaults to 'default'. */
  activeProfileId?: string;
  /** Parent-controlled settings. Added in v14. */
  parentSettings?: ParentSettings;
  /**
   * 玩家目前就讀的學年(學期),驅動「出考卷中心」只列當前學年的課本單元
   * (boss 2026-06-14:範圍清單只列三下 9 單元,其餘學年收進複習摺疊)。
   * 3.1 = 三上、3.2 = 三下、4.1 = 四上、4.2 = 四下。未填時 UI 預設三下(3.2),
   * 舊存檔免遷移。設定頁可切換;期末考過後 banner 提示升學年(只切此值,絕不動
   * 戰力 / 帳號數值)。Added 2026-06-14;4.2 + 升學年 Added 2026-06-10。 */
  currentGradeSemester?: GradeSemester;
  /**
   * 已 dismiss(或已升級)過「升學年」提示的學年清單 —— 一次性 banner 去重用。
   * 例:在三下按過「之後再說 / 升上四年級」⇒ 含 3.2,該學年不再自動彈 banner。
   * 純學習側留存旗標,不影響任何戰力 / 帳號數值。Added 2026-06-10。 */
  dismissedGradeUpgrade?: GradeSemester[];
  /**
   * Number of practice papers composed so far — acts as a logical clock for
   * 卷次驅動 (paper-count-driven) Leitner review, independent of the wall
   * calendar (LEARNING_SYSTEM_DESIGN_v2 §0 #2). Incremented by
   * `advancePracticePaperCount`. Coexists with the legacy date-based
   * `nextReviewISO` path; defaults to 0 when absent. Added in v2-review-slice. */
  practicePaperCount?: number;
  /** Tower cosmetic IDs (粒子 / 邊框 / 稱號) unlocked from infinite-tower
   *  floor milestones (每 10 層解鎖 1 件). Cosmetics are pure-visual — no
   *  combat-stat effect. Added in v16. See src/game/cosmetics/tower-cosmetics.ts
   *  for the catalog. */
  unlockedTowerCosmetics?: string[];
  /**
   * True after the player completes their FIRST skill level-up.
   * Drives the Pokémon-evolution-style overlay (fires once); subsequent
   * level-ups use inline pulse only. Lv5 graduation also re-triggers the
   * overlay regardless of this flag. Added in v17.
   */
  firstLevelUpDone?: boolean;
  /**
   * C05 Day-1 hook state (首日新手禮包 + 7 日簽到 + 教學 boss).
   * `firstLaunchAt`  — Unix ms when the player first opened the app (and
   *                    therefore became eligible for D1 of the 7-day calendar).
   *                    0 / missing means the player has not yet been seeded.
   * `claimedDays`    — list of 1..7 day numbers already claimed via the calendar.
   * `tutorialBossCompleted` — true after the kid has beaten the must-win
   *                    tutorial boss (`tutorial-boss`). Awards 200 freeXP.
   * Added in v18.
   */
  dailyHook?: {
    firstLaunchAt: number;
    claimedDays: number[];
    tutorialBossCompleted: boolean;
  };
  /**
   * C04 Badge + 解鎖儀式感 state (學習端 only).
   * `earned`         — list of badge ids already unlocked (idempotent dedupe).
   * `lastUnlocked`   — most recently unlocked badge id + Unix ms timestamp.
   *                    Used by mascot + home banner for the "🎉 解鎖新徽章!" reveal
   *                    on the next home render. Optional — undefined means no
   *                    pending reveal.
   * Added in v20. See src/learn/badges/badges.ts for unlock criteria + catalog.
   */
  badges?: {
    earned: string[];
    lastUnlocked?: { id: string; at: number };
  };
  /**
   * C06 — per-calendar-day question count, used by the parent dashboard's
   * 7d / 30d summary (`qCount` column of {@link DailyStat}). Key format
   * 'YYYY-MM-DD'. Added in v21 (originally targeted v20; bumped to v21
   * during the C04+C06 parallel-merge to keep the migration chain linear).
   * Incremented by `recordQuestionAnswered` which is wired into the generic
   * lesson runner + exam runner so every scored attempt bumps today's bucket.
   *
   * Why a separate map (and not a derivation from `studentKPs.totalAttempts`):
   * StudentKP totals are *cumulative* — they don't tell us how many were
   * answered ON a specific day, only the all-time count. The dashboard
   * needs daily granularity (e.g. "本週共答 N 題" / "30 天每日平均").
   */
  dailyQCount?: Record<string, number>;
  /**
   * C02 Battle Pass 季節軌道 — Prodigy Treasure Track-style retention loop.
   *
   * `currentSeason`     — 1-indexed season number (S1 / S2 / S3 …). Bumps on
   *                       auto-reset when `seasonStartedAt + seasonLengthDays`
   *                       has elapsed. Pure cosmetic counter for the UI label.
   * `seasonStartedAt`   — Unix ms timestamp when the CURRENT season began. 0
   *                       means uninitialised (lazy seed on first home tick).
   * `xpThisSeason`      — Accumulated seasonXP earned during the current
   *                       season. Drives tier unlocks; reset to 0 on season
   *                       reset. NOT the same as `totalXP`/`factionXP`
   *                       (which are lifetime curriculum metrics) — seasonXP
   *                       comes from retention events (streak milestone /
   *                       badge unlock / day-one claim / boss defeat etc.)
   *                       via `addSeasonXP`. This is meta-game currency, not
   *                       learning XP.
   * `claimedTiers`      — Tier numbers (1..N) already claimed in the current
   *                       season. Reset to [] on season reset so each new
   *                       season is a fresh claim arc.
   * `longestSeasonXp`   — Historical best `xpThisSeason` ever achieved. Used
   *                       by the season-pass UI 「歷史最佳:N XP」 line and
   *                       by parent dashboard for the loss-aversion framing.
   *                       Survives season reset — that's the whole point.
   * `seasonLengthDays`  — Length of one season in calendar days (default 30,
   *                       configurable per-season for testing / Phase 2
   *                       "weekly mini-season"). Persisted so a mid-season
   *                       length tweak doesn't retroactively re-reset.
   *
   * Why a separate field (and not folding into freeXP / faction-XP):
   * The CLAUDE.md 紅線 says learning-XP must stay tied to actual learning;
   * season meta-game rewards must NOT short-circuit faction-XP unlocks.
   * Keeping seasonXP a pure side-channel keeps the economy honest.
   * Added in v22.
   */
  seasonPass?: {
    currentSeason: number;
    seasonStartedAt: number;
    xpThisSeason: number;
    claimedTiers: number[];
    longestSeasonXp: number;
    seasonLengthDays: number;
  };
  /**
   * C03 怪獸收集 + 進化 — Prodigy/Pokémon-style retention meta-game.
   *
   * `caught`   — per-monster-id duplicate counter. Keys map to ids from
   *              src/learn/monsters/monsters-data.json (e.g.
   *              'celestial-comet-pup'). Value = how many duplicates the
   *              kid currently HOLDS (not lifetime count — fusing into
   *              an evolved form CONSUMES 3 duplicates). Missing key /
   *              0 value = not caught.
   * `lastNew`  — id + Unix-ms timestamp of the most recently caught OR
   *              evolved monster. Drives the home reveal banner (「✨ 抓到
   *              新怪獸:彗星幼犬!」). Consumed on render (cleared after one
   *              read) so subsequent home renders don't re-fire. Optional —
   *              undefined means no pending reveal.
   *
   * Why a separate field (not folded into badges):
   *   Different shape (per-id integer count vs boolean unlock), different
   *   semantics (catch CAN happen multiple times → evolution mechanic).
   *   Catching is a side-channel meta-game — does NOT touch faction-XP /
   *   freeXP. Pure 學習端 collection — never appears in src/game/.
   * Added in v23.
   */
  monsters?: {
    caught: Record<string, number>;
    lastNew?: { id: string; at: number };
  };
  /**
   * D-001 Phase A — 學科段位 + 學習積分 (per-subject XP / Lv1-10).
   *
   * `subjectXP`     — accumulated 學習積分 per subject. UI-facing source of
   *                   truth for the learn-entry app; factionXP stays the
   *                   game-side currency and is NOT touched by this field.
   *                   (D-001 §5.1 + 風險 R-001: 不雙寫 — Phase A only writes
   *                   subjectXP; faction-side sync is Phase D backlog.)
   *                   Missing key / 0 = brand new (Lv 1, 0/50 XP).
   * `subjectLevel`  — derived rank 1..10 (K-3 active range). Stored so the
   *                   migration can grandfather existing players in once and
   *                   subsequent reads don't have to recompute.
   * `xpHistory`     — FIFO log of XP deltas (Phase A populates lightly; Phase
   *                   C extends to LINE-bot weekly snapshot). Capped at 100
   *                   entries by `applyXpDelta` to keep localStorage small.
   * Added in v25 (D-001 Phase A).
   */
  subjectXP?: Partial<Record<Subject, number>>;
  subjectLevel?: Partial<Record<Subject, number>>;
  xpHistory?: Array<{
    /** ISO date 'YYYY-MM-DD'. */
    date: string;
    subject: Subject;
    /** Final XP awarded (already streak-multiplied + floored). */
    delta: number;
    /** Free-form audit string e.g. 'correct-d1' | 'correct-d2' | 'correct-d3' | 'milestone'. */
    reason: string;
  }>;
  /**
   * D-001 Phase B — Adaptive Difficulty Controller (Prodigy-style silent).
   *
   * Per-subject難度軌道:連對 3 題自動升一級、連錯 2 題自動降一級。
   * **完全靜默**:UI 0 提示、0 toast、0 動畫 (boss 鐵則 + Prodigy 哲學)。
   * Only active in 練習模式 (`mode: 'normal' | 'retry'`); formal exam (timer
   * 10/20) and challenge mode 完全不啟用 (D-001 §4 + Boss D3 拍板 A 選項)。
   *
   * Fields:
   *   `currentLevel`    — 目前推薦難度 1..3 (d1=暖身 / d2=中等 / d3=挑戰).
   *                       Missing key / new player → 1 (從暖身起步).
   *   `streakCorrect`   — 連對計數;升一級後 reset 0.
   *   `streakWrong`     — 連錯計數;降一級後 reset 0.
   *   `lastUpdated`     — ISO date string of last write (audit / future
   *                       session-reset hooks; Phase B 不做 30-min session
   *                       reset,留待 Phase C 配合 weekly snapshot).
   * Added in v26 (D-001 Phase B).
   */
  adaptiveDifficultyState?: {
    perSubject: Partial<Record<Subject, {
      currentLevel: 1 | 2 | 3;
      streakCorrect: number;
      streakWrong: number;
      lastUpdated: string;
    }>>;
  };
  /**
   * D-001 Phase C — pending push-notification events queue.
   *
   * Schema-only side-channel: the learn-entry app **enqueues** events when a
   * subject段位 reaches a milestone (Lv5 中段位 or Lv10 學期畢業 — only those
   * two are PUSH-worthy per D-001 §3.3 + D-005 §3.2). The downstream LINE
   * Bot worker (D-005 implementer) is responsible for **dequeuing** these and
   * issuing the actual push, after which entries are removed by the sync
   * handler. Phase C does NOT push anything itself — it only records the
   * intent so the family-sync round-trip can deliver it.
   *
   * Why only Lv5 / Lv10: every level-up would spam parents (~1-2 push/day at
   * peak); Lv5 (中段位徽章) and Lv10 (學期畢業) are the boss-approved family-
   * facing milestones (D-001 §3.1).
   *
   * Bounded: hard-capped at 20 entries (FIFO) to prevent runaway growth if
   * the worker is offline for an extended period.
   *
   * Added in v27 (D-001 Phase C).
   */
  pendingPushEvents?: Array<{
    type: 'level-up-milestone';
    subject: Subject;
    /** Only 5 or 10 — those are the boss-approved push-worthy milestones. */
    level: 5 | 10;
    /** ISO-8601 UTC timestamp (with ms + 'Z' suffix). */
    timestamp: string;
  }>;
  /**
   * D-001 Phase C — daily XP cap tracker (§2.5 防灌水).
   *
   * Per the spec, three caps apply to the learn-entry XP loop:
   *   1. 每日總 XP per subject ≤ 500 (across the whole day for one subject).
   *      Implementation note: we track the *across-all-subjects* total here
   *      because the boss prompt explicitly said "同日 totalXP > 500 → 截斷",
   *      not per-subject. Per-subject break-down is left to xpHistory analysis.
   *   2. 單題 XP ≤ 20 (post-streak-multiplier).
   *   3. 每日最多升 3 段 (per any subject combined).
   *
   * `date` is ISO date 'YYYY-MM-DD'; readers MUST compare to today's ISO and
   * reset to fresh shape ({date, 0, 0}) on date roll-over.
   *
   * `xpToday` accumulates the **awarded** delta (post-cap). `levelsToday`
   * counts level-up events triggered today (capped at 3 — further XP still
   * accumulates but does not bump subjectLevel until tomorrow).
   *
   * Added in v27 (D-001 Phase C).
   */
  dailyXpCap?: {
    /** ISO date 'YYYY-MM-DD' the counters belong to. */
    date: string;
    /** XP awarded today (post-cap). Reset to 0 on date roll-over. */
    xpToday: number;
    /** Level-ups applied today across all subjects. Cap = 3. */
    levelsToday: number;
  };
  // ── 玩遊戲軌(指揮官等級)— v29。純帳號廣度,絕不影響核心戰力。 ──
  //
  // 雙軌進度鐵則 (docs/spec/PROGRESSION_AND_ACCOUNTS.md §3.2 / §7):
  //   學習 = 戰力深度 (factionXP → 技能解鎖 / skillLevels 強度);
  //   玩遊戲 = 帳號廣度 (commanderXp → 英雄/造型/關卡/模式 + 封頂 QoL)。
  //   這些欄位 NEVER 寫入 factionXP / skillLevels / 傷害 / HP / 冷卻。
  //   全部 optional + 安全預設,向後相容 (migrate v28→29 backfill)。
  /** 指揮官 XP(玩遊戲軌)。來源:擊殺/波次/魔王/通關 (computeCommanderXp)。
   *  鐵則:NEVER 寫入 factionXP/skillLevels/傷害/HP。Added in v29. */
  commanderXp?: number;
  /** 由 commanderXp 推導的指揮官等級(1..30)。存起來避免每次 recompute;
   *  讀取一律以 computeCommanderLevel(commanderXp) 為權威(對齊 xp.ts pattern)。
   *  Added in v29. */
  commanderLevel?: number;
  /** 已解鎖英雄 id(門派)。預設 ['celestial'](= DEFAULT_HERO_ID),並含當前
   *  selectedHeroId。hero-select 改讀此清單決定鎖頭。Added in v29. */
  unlockedHeroes?: FactionId[];
  /** 已解鎖關卡 stage 編號(battle scene 的 stage)。預設 [1..5]。Added in v29. */
  unlockedStages?: number[];
  /** 已解鎖遊戲模式 id(如 'infinite-tower'|'extreme')。預設 []。Added in v29. */
  unlockedModes?: string[];
  /** 已解鎖配件 id(僚機/近戰/門派特殊)。玩遊戲軌「廣度」解鎖 → 進未來 run 可選池。
   *  鐵則:只記擁有資格,不灌永久戰力(配件戰力仍當局生效)。預設 []。
   *  Added 2026-06-06(P3 配件系統,optional 向後相容,不 bump version)。 */
  unlockedAccessories?: string[];
  /** 已解鎖武器類型 id(打贏魔王首殺解鎖)。玩遊戲軌「廣度」解鎖 → 進場內升級牌池
   *  (未解鎖不進池)。鐵則:只記「可使用該武器的資格」(廣度),武器戰鬥效果走單場
   *  RunModifiers(run 結束歸零),**絕不**寫 factionXP/freeXP/skillLevels/永久傷害。
   *  各武器是 sidegrade(不同彈道/玩法,整體強度相當),解鎖不讓同一關更易打贏。
   *  對應表見 src/game/progression/boss-weapon-unlocks.ts。預設 []。Added 2026-06-13。 */
  unlockedWeapons?: string[];
  /** 出戰前選的主武器 id(R-005 主武器系統)。場景啟動依此裝 WeaponBehavior:
   *  在 unlockedWeapons 內才用,否則回退預設 'rapid-cannon'(永遠可用)。
   *  鐵則放寬(boss 2026-06-13):武器脫離「學習=戰力」,威力由武器等級決定、不讀修煉值;
   *  此欄只記「選了哪把」,不碰 factionXP/freeXP/skillLevels。預設 'rapid-cannon'。Added 2026-06-13。 */
  selectedWeapon?: string;
  /** 每把主武器的等級(R-005 升級用)。威力 = base × 等級曲線(weaponLevelDamageMult),
   *  **不讀修煉值**。鐵則:武器養成是獨立遊戲化線,升級只寫此欄,絕不碰戰力欄位。
   *  預設 {}(每把缺省視為 Lv1)。Added 2026-06-13。 */
  weaponLevels?: Record<string, number>;
  /** 武器升級素材/貨幣(R-005 P2)。玩遊戲軌:戰鬥勝利結算發放(比照 commanderXp 寫法),
   *  花費於 weapon-select 升級畫面 → weaponLevels[id]++。鐵則:純武器養成貨幣,
   *  **絕不**等同/換算 factionXP/freeXP/skillLevels/修煉值;發放/扣除都不碰戰力欄位。
   *  預設 0。Added 2026-06-13。 */
  weaponShards?: number;
  /** 戰鬥端闖關進度(Candy Crush 線性關卡地圖)。**獨立**於學習端 campaignState,
   *  避免雙寫漂移(研究 STAGE_PROGRESSION_RESEARCH.md 注意點)。
   *  clearedStages=已通關關卡 id;stageStars=各關星等(1-3)。解鎖採線性推算
   *  (前一關 cleared → 下一關 unlocked),不另存 unlocked。Added 2026-06-06。 */
  battleCampaign?: { clearedStages: string[]; stageStars: Record<string, number> };
  /** 已解鎖純外觀造型 id(與既有 unlockedTowerCosmetics 並存,不合併以免漂移)。
   *  預設 []。Added in v29. */
  unlockedCommanderCosmetics?: string[];
  /** 封頂 QoL 輔助。每項都有 hard cap;只影響「便利」不影響戰力。Added in v29. */
  qol?: {
    /** 起手金幣(局內、打完即丟,見 horizontal-battle-scene coins 鐵則)。cap 100。 */
    startingCoins?: number;
    /** roguelite 三選一重抽次數/場。cap 2。 */
    rerollCharges?: number;
  };
  // ── 永久升級商店(PowerUp / meta-shop)— 玩遊戲軌。spec: LONG_TERM_PROGRESSION.md §7。──
  //
  // 雙軌進度鐵則(commander.ts NEVER 規範同款):這兩個欄位是**玩遊戲軌**,與學習軌欄位
  // (factionXP / freeXP / skillLevels)**物理分離**(放 commander/qol 旁,各自存)。
  // 套用見 src/game/progression/meta-shop.ts(applyMetaBonuses,每項硬封頂),
  // 經濟見 src/game/progression/meta-economy.ts(指數成本 + respec)。
  // 鐵則:meta 金幣/節點等級 **NEVER** 寫入/換算 factionXP/freeXP/skillLevels/修煉值。
  /** 永久 meta 金幣(玩遊戲軌貨幣)。來源:戰鬥結算 computeMetaCoins;花費於 meta-shop。
   *  與學習軌完全無關,不進任何傷害公式。預設 0。Added 2026-06-15。 */
  metaCoins?: number;
  /** 各英雄(門派)meta 小樹的節點等級:metaShop[heroId][nodeId] = 等級。
   *  每節點有 maxLevel + 硬封頂(applyMetaBonuses 夾住)。空 = 全 Lv0(底盤未抬)。
   *  鐵則:只是「買了幾級」記錄,套用倍率時各項硬封頂,絕不溢出成無限戰力。
   *  預設 {}。Added 2026-06-15。 */
  metaShop?: Record<string, Record<string, number>>;
}

/**
 * C06 parent dashboard — one row per calendar day. Synthesised by
 * `buildDailyStats` from the three independent per-day maps:
 *   • `dailyXP`              — xpGained
 *   • `dailyQCount`          — qCount (added v21, originally v20)
 *   • `parentSettings.dailyPlayMinutes` — sessionMin
 * The array is dense (every day in the window, even zero-activity days)
 * and sorted ascending by date.
 */
export interface DailyStat {
  /** ISO calendar date 'YYYY-MM-DD'. */
  date: string;
  /** Questions answered (scored) on this day. */
  qCount: number;
  /** XP earned on this day (sum of faction + free XP grants). */
  xpGained: number;
  /** Minutes played on this day (game-time, learn-time both fold in). */
  sessionMin: number;
}

/** Daily game time limit options (minutes). 0 = unlimited. */
export type DailyTimeLimitMinutes = 0 | 30 | 45 | 60 | 90;

export interface ParentSettings {
  /** Daily game time limit in minutes. 0 means unlimited. */
  dailyTimeLimitMinutes: DailyTimeLimitMinutes;
  /** ISO date of last game session (YYYY-MM-DD). Used to reset daily counter. */
  lastPlayDate: string;
  /** Minutes played today (resets at midnight). */
  minutesPlayedToday: number;
  /** Parent PIN protecting the 👪 家長管控 + 🔒 隱私管理 sections from accidental
   *  kid taps. Undefined → not yet set; first visit prompts setup.
   *  Stored as SHA-256 hex (64 lowercase hex chars) from v15+. Legacy v15 records
   *  may carry a 4-digit plaintext form; both are accepted by `verifyParentPin`. */
  parentPin?: string;
  /** When true, the 修煉學院 entry from this profile loads in parent-mode (quantitative
   *  mastery bars instead of kid-friendly "今天的章節" labels). Added in v15. */
  parentMode?: boolean;
  /** Per-day minutes-played history. Key format `YYYY-MM-DD`. Updated by
   *  `recordPlayMinutes`. Used by parent-dashboard weekly view. Added in v15. */
  dailyPlayMinutes?: Record<string, number>;
  /** When true, expose Phase 2 「實驗中」 home cards (戰役 / 模擬考 / 無限魔王塔 /
   *  塔記錄板) — for parents who want to demo non-MVP features. Default undefined =
   *  MVP_MODE on (those 4 cards hidden so kids only see 2 hero CTAs + 5 core cards).
   *  Toggle in settings 👪 家長管控 → 🧪 體驗模式. Added in v15.1 (task #49). */
  experimentalMode?: boolean;
  /**
   * 教學專業模式 (Pro Mode) — when true, the 答題執行畫面 strips out all
   * 遊戲化 affordances (mascot / SFX / BGM / faction 修煉值 framing / colored
   * flash banners / drama animations) and shows a plain professional exam UI.
   *
   * Boss directive (2026-05-22): 「教學介面應該要正式一點,教學介面不需要遊戲
   * 化、不需要有音樂。教學是要專業的」.
   *
   * Default behaviour: TRUE (professional). Legacy/migrated saves where the
   * field is undefined are treated as proMode=true by `isProMode()` in
   * settings.ts. Parents can opt INTO the legacy gamified experience by
   * unticking the toggle in 設定 → 📚 教學介面. Added in v24. */
  proMode?: boolean;
  /**
   * 減少晃動 (Reduce Motion) — when true, the battle scene damps screen-shake
   * and hit-stop so the camera barely moves. Parent/accessibility friendly:
   * K-3 players (the target audience) are prone to motion sickness from the
   * larger "juice" shakes tuned for adult games.
   *
   * Purely presentational — never touches 戰力 / progress / damage / HP / cooldown.
   * Read game-side via `motion-prefs.ts` (the battle scene pushes this flag in
   * on create) and learn-side via `isReduceMotion()` in settings.ts.
   *
   * Default: FALSE (undefined ⇒ off) so the current hand-tuned feel is preserved
   * for everyone; parents opt INTO the calmer mode. Added in v25. */
  reduceMotion?: boolean;
}

/** Reason a tower run ended. Added in v16 (W4-TOWER cosmetics + death tracking). */
export type TowerRunRetiredAt = 'death' | 'manual' | 'time-out';

export interface TowerRun {
  highestFloor: number;
  totalSec: number;
  totalKills: number;
  /** floor*100 + kills*5 + max(0, 3600 - totalSec) */
  score: number;
  /** Unix timestamp (ms) when the run ended. */
  endedAt: number;
  /** How many times the player died during this run. Added in v16. */
  deathCount?: number;
  /** Reason the run ended. Added in v16. */
  retiredAt?: TowerRunRetiredAt;
}

export type LeitnerBox = 1 | 2 | 3 | 4 | 5;

export interface StudentKP {
  kpId: string;                  // e.g. 'math.mul.2x1'
  subject: Subject;
  totalAttempts: number;
  correctCount: number;
  carelessCount: number;
  lastReviewedISO: string;       // empty string if never reviewed
  box: LeitnerBox;               // 1 = daily, 5 = monthly
  nextReviewISO: string;         // computed from box interval
  source: 'prepared' | 'exam-upload' | 'archive';
  /** IDs of questions shown in recent sessions; spaced-review avoids repeating. */
  lastSeenQuestionIds: string[];
  /**
   * Paper-count clock at which this KP is next due for review
   * (LEARNING_SYSTEM_DESIGN_v2 §0 #2). Compared against
   * `PlayerProgress.practicePaperCount` via `isDueByPaper`; set by
   * `recordAttemptV2`. Coexists with the date-based `nextReviewISO`.
   * `undefined`/`null` means "due now". Added in v2-review-slice. */
  dueAtPaper?: number;
}
